import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from './usePermissions';

type SecureQueryOptions<T> = {
  requiredPermission?: string;
  requiresAuth?: boolean;
  onUnauthorized?: () => void;
} & Omit<UseQueryOptions<T>, 'queryFn' | 'queryKey'>;

export const useSecureQuery = <T,>(
  queryKey: unknown[],
  queryFn: () => Promise<T>,
  options: SecureQueryOptions<T> = {}
) => {
  const { user, hasPermission, loading } = usePermissions();
  
  const {
    requiredPermission,
    requiresAuth = true,
    onUnauthorized,
    ...queryOptions
  } = options;

  const isAuthorized = () => {
    if (requiresAuth && !user) return false;
    if (requiredPermission && !hasPermission(requiredPermission)) return false;
    return true;
  };

  const shouldFetch = !loading && (requiresAuth ? !!user : true);

  return useQuery({
    ...queryOptions,
    queryKey,
    queryFn: async () => {
      if (!isAuthorized()) {
        onUnauthorized?.();
        throw new Error('Unauthorized access');
      }
      return queryFn();
    },
    enabled: shouldFetch && isAuthorized() && (queryOptions.enabled ?? true)
  });
};